import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieBanner() {
  const { t, i18n } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });

  const getTexts = () => {
    if (i18n.language === 'es') {
      return {
        title: 'Configuración de Cookies',
        description: 'Utilizamos cookies para mejorar su experiencia de navegación. Puede aceptar todas las cookies, rechazarlas o personalizar su configuración.',
        acceptAll: 'Aceptar Todas',
        rejectAll: 'Rechazar Todas',
        settings: 'Configuración',
        savePreferences: 'Guardar Preferencias',
        categories: {
          necessary: {
            title: 'Cookies Necesarias',
            description: 'Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.'
          },
          functional: {
            title: 'Cookies Funcionales',
            description: 'Estas cookies permiten funcionalidades mejoradas y personalización.'
          },
          analytics: {
            title: 'Cookies de Análisis',
            description: 'Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.'
          },
          marketing: {
            title: 'Cookies de Marketing',
            description: 'Estas cookies se utilizan para rastrear visitantes en sitios web para mostrar anuncios relevantes.'
          }
        }
      };
    }

    if (i18n.language === 'de') {
      return {
        title: 'Cookie-Einstellungen',
        description: 'Wir verwenden Cookies, um Ihr Browsing-Erlebnis zu verbessern. Sie können alle Cookies akzeptieren, ablehnen oder Ihre Einstellungen anpassen.',
        acceptAll: 'Alle akzeptieren',
        rejectAll: 'Alle ablehnen',
        settings: 'Einstellungen',
        savePreferences: 'Einstellungen speichern',
        categories: {
          necessary: {
            title: 'Notwendige Cookies',
            description: 'Diese Cookies sind für die Funktion der Website unerlässlich und können nicht deaktiviert werden.'
          },
          functional: {
            title: 'Funktionale Cookies',
            description: 'Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung.'
          },
          analytics: {
            title: 'Analyse-Cookies',
            description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.'
          },
          marketing: {
            title: 'Marketing-Cookies',
            description: 'Diese Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen, um relevante Anzeigen anzuzeigen.'
          }
        }
      };
    }

    return {
      title: 'Cookie Settings',
      description: 'We use cookies to enhance your browsing experience. You can accept all cookies, reject them, or customize your settings.',
      acceptAll: 'Accept All',
      rejectAll: 'Reject All',
      settings: 'Settings',
      savePreferences: 'Save Preferences',
      categories: {
        necessary: {
          title: 'Necessary Cookies',
          description: 'These cookies are essential for the website to function and cannot be disabled.'
        },
        functional: {
          title: 'Functional Cookies',
          description: 'These cookies enable enhanced functionality and personalization.'
        },
        analytics: {
          title: 'Analytics Cookies',
          description: 'These cookies help us understand how visitors interact with our website.'
        },
        marketing: {
          title: 'Marketing Cookies',
          description: 'These cookies are used to track visitors across websites to display relevant advertisements.'
        }
      }
    };
  };

  const texts = getTexts();

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    savePreferences(onlyNecessary);
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[600px] w-full p-8">
        {!showSettings ? (
          <>
            <h3 className="text-2xl font-semibold text-[#0A2D50] mb-4">
              {t('cookies.title')}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed mb-6">
              {t('cookies.description')}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={acceptAll}
                className="w-full bg-[#1BCDFF] text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#1EB7EC] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('cookies.acceptAll')}
              </button>
              <button
                onClick={rejectAll}
                className="w-full bg-[#0A2D50] text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#0A2D50]/90 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('cookies.rejectAll')}
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="w-full border-2 border-[#0A2D50] text-[#0A2D50] px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#F2F4F5] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('cookies.settings')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-2xl font-semibold text-[#0A2D50] mb-6">
              {t('cookies.settingsTitle')}
            </h3>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-4 bg-[#F2F4F5] rounded-lg">
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2D50] mb-1">
                    {t('cookies.necessary')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('cookies.necessaryDesc')}
                  </p>
                </div>
                <div className="text-xs font-medium text-gray-500">
                  {i18n.language === 'de' ? 'Immer aktiv' : 'Always active'}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F2F4F5] rounded-lg">
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2D50] mb-1">
                    {t('cookies.functional')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('cookies.functionalDesc')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1BCDFF]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F2F4F5] rounded-lg">
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2D50] mb-1">
                    {t('cookies.analytics')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('cookies.analyticsDesc')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1BCDFF]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F2F4F5] rounded-lg">
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2D50] mb-1">
                    {t('cookies.marketing')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('cookies.marketingDesc')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1BCDFF]"></div>
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 border-2 border-[#0A2D50] text-[#0A2D50] px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#F2F4F5] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {i18n.language === 'de' ? 'Zurück' : 'Back'}
              </button>
              <button
                onClick={saveCustom}
                className="flex-1 bg-[#1BCDFF] text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#1EB7EC] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {i18n.language === 'de' ? 'Speichern' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}